'use client';

import { cn } from '@core/utils';
import type { GiftOrderProductReviewDto } from '@generated/auth';
import type {
  DetailContentInfo,
  DetailHomeGiftProductResponse,
} from '@generated/unauth';

import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/_components/DonationLayout/DonationSubHeader';
import type { GiftSaleStatus } from '@/app/donation/types';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { TABS } from '../../constants';
import type { GiftDetailPageProps } from '../../page';
import { GiftDetailTab } from '../GiftDetailTab';
import { GiftTabContentBasic } from '../GiftTabContent/_components/GiftTabContentBasic';
import { GiftTabContentDelivery } from '../GiftTabContent/_components/GiftTabContentDelivery';
import { GiftTabContentInfo } from '../GiftTabContent/_components/GiftTabContentInfo';
import { GiftTabContentInquiry } from '../GiftTabContent/_components/GiftTabContentInquiry';
import { GiftTabContentReview } from '../GiftTabContent/_components/GiftTabContentReview';
import { HeaderInfoSection } from './HeaderInfoSection';
import { PrefillForm } from './PrefillForm';
import { ResponsiveDivider } from './ResponsiveDivider';
import { ReviewShortcutSection } from './ReviewShortcutSection';

interface GiftDetailTemplateProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionId: number;
  institutionLogoUrl: string;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  searchParams?: GiftDetailPageProps['searchParams'];
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  reviewList: GiftOrderProductReviewDto[];
  detailContentInfoList: DetailContentInfo[];
  productInfo: string;
  deliveryCompanyName: string;
  productData?: DetailHomeGiftProductResponse;
  noticeTitle?: string;
  noticeContents?: string;
}

const GIFT_DETAIL_SIDEBAR_STICKY_TOP = 218;

export const GiftDetailTemplate = ({
  title,
  heroImageUrlList,
  adultYN,
  institutionId,
  institutionLogoUrl,
  institutionName,
  productSaleStatus,
  productName,
  reviewCount,
  reviewRating,
  productNumber,
  requiredDonationAmount,
  searchParams,
  exchangeYN,
  onlineProductYN,
  onlineProductDesc,
  reviewList,
  detailContentInfoList,
  productInfo,
  deliveryCompanyName,
  productData,
  noticeTitle,
  noticeContents,
}: GiftDetailTemplateProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  const disabledTabs = [...(reviewCount < 1 ? [TABS.REVIEW] : [])];

  return (
    <div
      className={cn(
        'mt-20 content-container',
        'tablet:mt-0 tablet:px-0',
        'mobile:mt-0 mobile:px-0',
      )}
    >
      <HeaderInfoSection
        title={title}
        heroImageUrlList={heroImageUrlList}
        adultYN={adultYN}
        institutionId={institutionId}
        institutionLogoUrl={institutionLogoUrl}
        institutionName={institutionName}
        productSaleStatus={productSaleStatus}
        productName={productName}
        reviewCount={reviewCount}
        reviewRating={reviewRating}
        productNumber={productNumber}
        requiredDonationAmount={requiredDonationAmount}
        exchangeYN={exchangeYN}
        onlineProductYN={onlineProductYN}
        onlineProductDesc={onlineProductDesc}
        productData={productData}
        className={cn('mb-100 px-40', 'mobile-tablet:mb-20 mobile-tablet:px-0')}
      />
      {isDesktopViewport ? (
        <ReviewShortcutSection
          reviewCount={reviewCount}
          items={reviewList}
          className={cn('mb-100 px-40', 'mobile-tablet:px-0')}
        />
      ) : null}
      <GiftDetailTab
        productNumber={productNumber}
        disabledTabs={disabledTabs}
        className={cn(
          'sticky z-10 mx-40 bg-Gray-white',
          'mobile-tablet:top-[48px] mobile-tablet:mx-0',
        )}
        style={
          isDesktopViewport ? { top: DONATION_DESKTOP_STICKY_TOP } : undefined
        }
      />
      <div
        className={cn(
          'mt-40 grid grid-cols-[1fr_370px] gap-80 px-40',
          'mobile-tablet:mt-0 mobile-tablet:grid-cols-1 mobile-tablet:px-0',
        )}
      >
        <div className="flex min-w-0 flex-col">
          {/* 상품 상세 */}
          <GiftTabContentBasic
            detailContentInfoList={detailContentInfoList}
            productName={productName}
            noticeTitle={noticeTitle}
            noticeContents={noticeContents}
          />
          <ResponsiveDivider />
          {/* 상품정보 */}
          <GiftTabContentInfo productInfo={productInfo} />
          <ResponsiveDivider />
          {/* 구매/배송 */}
          <GiftTabContentDelivery deliveryCompanyName={deliveryCompanyName} />
          <ResponsiveDivider />
          {/* 후기 */}
          <GiftTabContentReview
            reviewRating={reviewRating}
            reviewCount={reviewCount}
            productNumber={productNumber}
            searchParams={searchParams}
          />
          <ResponsiveDivider />
          {/* 문의 */}
          <GiftTabContentInquiry productNumber={productNumber} />
        </div>
        <div className="mobile:hidden tablet:hidden">
          <div
            className="sticky mb-44 rounded-[36px] border-1 px-28 py-44"
            style={{ top: GIFT_DETAIL_SIDEBAR_STICKY_TOP }}
          >
            <PrefillForm
              productData={productData}
              productNumber={productNumber}
              className="gap-80"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
